// @ts-nocheck
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import Block, { BlockProps } from "@store/Library/Components/Toolkit/Block";
import { infoProps } from "@store/Themes/common/props/info";
import { orderProps } from "@store/Themes/common/props/order";
import getWidgetComponent from "@toolkit/Framework/Common/Utilities/getWidgetComponent";
import { useSession, useSettingsCollection } from "@toolkit/Framework/Store/Context/ContextHooks";
import { useBillToState } from "@toolkit/Framework/Store/Data/BillTos/BillTosHooks";
import { useCartState } from "@toolkit/Framework/Store/Data/Carts/CartsHooks";
import { useOnePageCheckoutConfirmation } from "@toolkit/Framework/Store/Toolkit/Pages/OnePageCheckout/OnePageCheckoutHooks";
import React, { FC } from "react";

const PaymentMethod = getWidgetComponent("OnePageCheckoutConfirmation/OnePageCheckoutConfirmationPaymentMethod");

export interface OnePageCheckoutConfirmationOrderInformationProps extends WidgetProps {}

export interface OnePageCheckoutConfirmationOrderInformationStyles {
    orderNumberHeading?: TypographyProps;
    orderNumberText?: TypographyPresentationProps;
    vatNumberGridItem?: GridItemProps;
    notesGridItem?: GridItemProps;
    orderDetailsGridContainer?: GridContainerProps;
    statusGridItem?: GridItemProps;
    orderDateGridItem?: GridItemProps;
    poNumberGridItem?: GridItemProps;
    headingAndTextStyles?: SmallHeadingAndTextStyles;
    block?: BlockProps;
    deliveryMethodGridItem?: GridItemProps;
    paymentMethodGridItem?: GridItemProps;
}

export const onePageCheckoutConfirmationOrderInformationStyles: OnePageCheckoutConfirmationOrderInformationStyles = {
    block: orderProps.block,
    orderNumberHeading: orderProps.subtitle,
    headingAndTextStyles: {
        heading: infoProps.label,
        text: infoProps.value,
    },
    orderDetailsGridContainer: infoProps.grid?.container,
    orderDateGridItem: infoProps.grid?.item,
    poNumberGridItem: infoProps.grid?.item,
    statusGridItem: infoProps.grid?.item,
    vatNumberGridItem: infoProps.grid?.item,
    deliveryMethodGridItem: infoProps.grid?.item,
    paymentMethodGridItem: infoProps.grid?.item,
    notesGridItem: infoProps.grid?.largeItem,
};

const styles = onePageCheckoutConfirmationOrderInformationStyles;

const OnePageCheckoutConfirmationOrderInformation: FC<OnePageCheckoutConfirmationOrderInformationProps> = () => {
    const { cartId } = useOnePageCheckoutConfirmation();
    const cartState = useCartState(cartId).value;
    const { enableVat } = useSettingsCollection().productSettings;
    const billTo = useBillToState(cartState?.billToId).value;
    const { language } = useSession();

    if (!cartState || !billTo) {
        return null;
    }

    return (
        <Block {...styles.block}>
            <Typography {...styles.orderNumberHeading}>
                {`${translate("Order")} #`}
                <Typography {...styles.orderNumberText}>{cartState.erpOrderNumber || cartState.orderNumber}</Typography>
            </Typography>
            <GridContainer {...styles.orderDetailsGridContainer}>
                <GridItem {...styles.orderDateGridItem}>
                    <SmallHeadingAndText
                        extendedStyles={styles.headingAndTextStyles}
                        heading={translate("Order Date")}
                        text={
                            cartState?.orderDate
                                ? getLocalizedDateTime({
                                      dateTime: new Date(cartState.orderDate as Date),
                                      language,
                                  })
                                : ""
                        }
                    />
                </GridItem>
                {cartState.poNumber ? (
                    <GridItem {...styles.poNumberGridItem}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("PO Number")}
                            text={cartState.poNumber}
                        />
                    </GridItem>
                ) : null}
                {cartState.status ? (
                    <GridItem {...styles.statusGridItem}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Status")}
                            text={cartState.statusDisplay}
                        />
                    </GridItem>
                ) : null}
                {enableVat && cartState.customerVatNumber ? (
                    <GridItem {...styles.vatNumberGridItem}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("VAT Number")}
                            text={cartState.customerVatNumber}
                        />
                    </GridItem>
                ) : null}
                <GridItem {...styles.deliveryMethodGridItem}>
                    <SmallHeadingAndText
                        extendedStyles={styles.headingAndTextStyles}
                        heading={translate("Delivery method")}
                        text={
                            cartState.fulfillmentMethod === FulfillmentMethod.PickUp
                                ? translate("Store pickup at ") +
                                  (cartState.defaultWarehouse?.description || cartState.defaultWarehouse?.name || "")
                                : cartState.carrier && cartState.shipVia
                                  ? `${translate("Ship to home via")} ${cartState.carrier.description} - ${
                                        cartState.shipVia.description
                                    }`
                                  : translate("Ship to home")
                        }
                    />
                </GridItem>
                <GridItem {...styles.paymentMethodGridItem}>
                    <PaymentMethod />
                </GridItem>
            </GridContainer>
            {cartState.notes ? (
                <GridContainer {...styles.orderDetailsGridContainer}>
                    <GridItem {...styles.notesGridItem}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Notes")}
                            text={cartState.notes}
                        />
                    </GridItem>
                </GridContainer>
            ) : null}
        </Block>
    );
};

const widgetModule: WidgetModule = {
    component: OnePageCheckoutConfirmationOrderInformation,
    definition: {
        displayName: "Order Information",
        allowedContexts: ["OnePageCheckoutConfirmationPage"],
        group: "One Page Checkout Confirmation",
    },
};

export default widgetModule;
